// Жалобы на сообщения: список и решение.
//
// Требование магазина к приложениям с перепиской (правило 1.2): пожаловаться можно из
// телефона, а разобрать — здесь. В условиях использования обещан ответ в течение суток,
// поэтому список открывается неразобранными и показывает, сколько жалоба уже ждёт.
//
// Снимок текста показывается ВМЕСТО живого сообщения намеренно: автор может удалить его
// через минуту после жалобы, и тогда разбирать было бы нечего.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { request } from '@/shared/api/client';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ScreenState } from '@/shared/ui/screen-state';
import { formatDateOnly } from '@/shared/time/format';

interface Жалоба {
  id: string;
  message_id: string | null;
  reason: string;
  note: string | null;
  snapshot: string | null;
  source: string;
  created_at: string;
  reported_by_name: string | null;
  author_name: string | null;
}

const ПОВОДЫ: Record<string, string> = {
  offensive: 'Оскорбление или угроза',
  spam: 'Спам, не по делу',
  fraud: 'Обман, просят деньги',
  other: 'Другое',
};

const РЕШЕНИЯ: { код: string; подпись: string; опасное?: boolean }[] = [
  { код: 'removed', подпись: 'Убрать сообщение', опасное: true },
  { код: 'warned', подпись: 'Предупредить автора' },
  { код: 'rejected', подпись: 'Повода нет' },
];

export function ЖалобыНаПереписку() {
  const клиент = useQueryClient();
  const [занята, setЗанята] = useState<string | null>(null);

  const жалобы = useQuery({
    queryKey: ['chat', 'reports'],
    queryFn: () =>
      request<{ items: Жалоба[]; total: number }>('/api/v1/chat/reports?open_only=true'),
  });

  const решить = useMutation({
    mutationFn: ({ id, решение }: { id: string; решение: string }) =>
      request(`/api/v1/chat/reports/${id}/resolve`, {
        method: 'POST',
        body: { resolution: решение },
      }),
    onSettled: () => {
      setЗанята(null);
      void клиент.invalidateQueries({ queryKey: ['chat', 'reports'] });
    },
  });

  const строки = жалобы.data?.items ?? [];

  return (
    <Block
      title='Жалобы на переписку'
      note={
        строки.length === 0
          ? 'Неразобранных нет'
          : `Неразобранных: ${строки.length}. Обещанный срок ответа — сутки`
      }
    >
      <ScreenState
        data={жалобы.data}
        error={жалобы.error as never}
        subject='список жалоб на переписку'
        isEmpty={(данные) => данные.items.length === 0}
        loading={<p className='text-paragraph-sm text-text-sub-600'>Смотрим жалобы…</p>}
        empty={
          <p className='text-paragraph-sm text-text-sub-600'>
            Пусто — и это нормальное состояние. Жалобы приходят из приложения: человек выбирает
            сообщение и нажимает «Пожаловаться». Сюда же попадает то, что поймал отсев
            непристойного.
          </p>
        }
        onRetry={() => void жалобы.refetch()}
      >
        {(данные) => (
        <ul className='flex flex-col gap-3'>
          {данные.items.map((жалоба) => (
            <li
              key={жалоба.id}
              className='flex flex-col gap-2 rounded-xl bg-bg-weak-50 p-4 text-paragraph-sm'
            >
              <div className='flex flex-wrap items-center gap-2 text-text-sub-600'>
                <span className='text-label-sm text-text-strong-950'>
                  {ПОВОДЫ[жалоба.reason] ?? жалоба.reason}
                </span>
                <span>· {formatDateOnly(жалоба.created_at.slice(0, 10))}</span>
                {жалоба.source === 'filter' && <span>· поймал отсев</span>}
                {жалоба.author_name !== null && <span>· автор: {жалоба.author_name}</span>}
                {жалоба.reported_by_name !== null && (
                  <span>· пожаловался: {жалоба.reported_by_name}</span>
                )}
              </div>

              {жалоба.snapshot !== null && (
                <p className='whitespace-pre-wrap text-text-strong-950'>{жалоба.snapshot}</p>
              )}
              {жалоба.note !== null && (
                <p className='text-text-sub-600'>Пояснение: {жалоба.note}</p>
              )}

              <div className='flex flex-wrap gap-2'>
                {РЕШЕНИЯ.map(({ код, подпись, опасное }) => (
                  <Button.Root
                    key={код}
                    type='button'
                    variant={опасное === true ? 'error' : 'neutral'}
                    mode='stroke'
                    size='xsmall'
                    disabled={занята === жалоба.id}
                    onClick={() => {
                      setЗанята(жалоба.id);
                      решить.mutate({ id: жалоба.id, решение: код });
                    }}
                  >
                    {подпись}
                  </Button.Root>
                ))}
              </div>
            </li>
          ))}
        </ul>
        )}
      </ScreenState>
    </Block>
  );
}
